/**
* Reverse Engineered from tabbable 5.2.0
*
* Would just import, but angular-ui-bootstrap doesn't support imports.
* Assumptions that simplify the code a lot:
* - The includeContainer option is always false
* - The displayCheck option is always 'none' which case 2 false positives:
*    - It doesn't check display:none up the DOM tree
*    - Neither does it check non zero width/height
* - It doesn't handle <details>/<summary> (html's native collapse) correctly
*    - It assumes <details> will always have a <summary>
*    - It has false positives of descentant nodes when details are collaped
*
* @license MIT, https://github.com/focus-trap/tabbable/blob/master/LICENSE
*/
const focusable = function (el) {
  const candidateSelector = [
    'input:not([type="hidden"])',
    'select',
    'textarea',
    'a[href]',
    'button',
    '[tabindex]',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    'details>summary:first-of-type'
  ].join(',');

  return Array.from(el.querySelectorAll(candidateSelector)).filter((node) =>
    !node.disabled && getComputedStyle(node).visibility !== 'hidden'
  );
};
/**
* End tabbable
*/

angular.module('ui.bootstrap.dropdown', ['ui.bootstrap.multiMap', 'ui.bootstrap.position'])

.constant('uibDropdownConfig', {
  appendToOpenClass: 'uib-dropdown-open',
  activeItemClass: 'uib-active-item',
  openClass: 'open'
})

.service('uibDropdownService', ['$document', '$rootScope', '$$multiMap', '$timeout', 'uibDropdownConfig', function($document, $rootScope, $$multiMap, $timeout, dropdownConfig) {
  var openScope = null;
  var openedContainers = $$multiMap.createNew();
  var self = this;

  this.isOnlyOpen = function(dropdownScope, appendTo) {
    var openedDropdowns = openedContainers.get(appendTo);
    if (openedDropdowns) {
      var openDropdown = openedDropdowns.reduce(function(toClose, dropdown) {
        if (dropdown.scope === dropdownScope) {
          return dropdown;
        }

        return toClose;
      }, {});
      if (openDropdown) {
        return openedDropdowns.length === 1;
      }
    }

    return false;
  };

  this.open = function(dropdownScope, element, appendTo) {
    if (!openScope) {
      $document.on('click', this.closeDropdown);
      element.on('keydown', this.keybindFilter);
    }

    if (openScope && openScope !== dropdownScope) {
      openScope.isOpen = false;
    }

    openScope = dropdownScope;

    if (!appendTo) {
      return;
    }

    var openedDropdowns = openedContainers.get(appendTo);
    if (openedDropdowns) {
      var openedScopes = openedDropdowns.map(function(dropdown) {
        return dropdown.scope;
      });
      if (openedScopes.indexOf(dropdownScope) === -1) {
        openedContainers.put(appendTo, {
          scope: dropdownScope
        });
      }
    } else {
      openedContainers.put(appendTo, {
        scope: dropdownScope
      });
    }
  };

  this.close = function(dropdownScope, element, appendTo) {
    if (openScope === dropdownScope) {
      $document.off('click', this.closeDropdown);
      element.off('keydown', this.keybindFilter);
      openScope = null;
    }

    if (!appendTo) {
      return;
    }

    var openedDropdowns = openedContainers.get(appendTo);
    if (openedDropdowns) {
      var dropdownToClose = openedDropdowns.reduce(function(toClose, dropdown) {
        if (dropdown.scope === dropdownScope) {
          return dropdown;
        }

        return toClose;
      }, {});
      if (dropdownToClose) {
        openedContainers.remove(appendTo, dropdownToClose);
      }
    }
  };

  // May be called when:
  // * $document.on('click')
  // * Escape, Enter, or Space is pressed
  // * Focusing off the end or start of the menu
  this.closeDropdown = function(evt) {
    // This method may still be called during the same mouse event that
    // unbound this event handler. So check openScope before proceeding.
    var dropdownElement = openScope.getDropdownElement();
    const listbox = dropdownElement.attr('role') === 'listbox';

    if (!openScope || !openScope.isOpen) { return; }

    // Get the autoclose setting, but override so that Escape always exits
    var autoClose = openScope.getAutoClose();
    if (evt.type === 'keydown' && evt.key === 'Escape') {
      autoClose = 'always';
    }

    // Don't close if the setting is disabled (unless escape is pushed)
    if (autoClose === 'disabled') { return; }

    // Don't close on right click
    if (evt.which === 3) { return; }

    // The toggle element handles its own state
    var toggleElement = openScope.getToggleElement();
    if (toggleElement && toggleElement[0].contains(evt.target)) {
      return;
    }

    // Don't close if the setting is outsideClick and the event was triggered inside
    if (autoClose === 'outsideClick' &&
      dropdownElement && dropdownElement[0].contains(evt.target)) {
      return;
    }

    // Now we have decided to close

    // Stop propogating the event
    evt.stopPropagation();

    // We want to prevent default in certain cases:
    // * Escape: exiting fullscreen
    // * Space: scrolling down
    if (evt.key === 'Escape' || evt.key === ' ') {
      evt.preventDefault();
    }

    // Enter on keyup emits a fake MouseEvent which triggers links/buttons.
    // We were originally preventDefaulting this, but that broke real <a> links.
    // If we don't preventDefault, the focus would be shifted to the toggle element
    // first, then keyup would fire the MouseEvent on the toggle which resets isOpen
    // before the $digest cycle completes, breaking the dropdown by having isOpen true,
    // but the dropdown would be hidden, which could cause background selection.
    // So we delay the focusToggleElement to next tick to satisfy both cases
    // Also we can't call openScope.focusToggleElement, as the scope is
    // already cleaned up by then.
    if (toggleElement && evt.key !== 'Tab') {
      $timeout(function () {
        toggleElement[0].focus();
      });
    }

    // Close the menu
    openScope.isOpen = false;

    if (!$rootScope.$$phase) {
      openScope.$apply();
    }
  };

  /**
   * Handle when keybinding events are made on the dropdown
   * @param {KeyboardEvent} evt
   */
  this.keybindFilter = function(evt) {
    if (!openScope) {
      // see this.close as ESC could have been pressed which kills the scope so we can not proceed
      return;
    }

    var dropdownElement = openScope.getDropdownElement();
    var toggleElement = openScope.getToggleElement();
    var dropdownElementTargeted = dropdownElement && dropdownElement[0].contains(evt.target);
    var toggleElementTargeted = toggleElement && toggleElement[0].contains(evt.target);

    if (['Tab', 'Escape', 'Enter', ' '].indexOf(evt.key) !== -1) {
      // Because focus is on the ul instead of the li, we need to trigger the action directly
      if ((evt.key === ' ' || evt.key === 'Enter') && dropdownElement.attr('role') === 'listbox') {
        // We'll just reemit the event on the child, but don't let it bubble (or it'd get to the ul and infinite loop)
        Object.defineProperty(evt, 'bubbles', {
          value: false,
          enumerable: true
        });
        dropdownElement[0].querySelector(`.${dropdownConfig.activeItemClass}`)
          .dispatchEvent(new KeyboardEvent(evt.type, evt));
      }

      self.closeDropdown(evt);
    } else if (['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].indexOf(evt.key) !== -1 &&
      openScope.isOpen &&
      (dropdownElementTargeted || toggleElementTargeted)) {
      evt.stopPropagation();
      openScope.handleKeyboardNav(evt);
    }
  };
}])

.controller('UibDropdownController', ['$scope', '$element', '$attrs', '$parse', 'uibDropdownConfig', 'uibDropdownService', '$animate', '$uibPosition', '$document', '$compile', '$templateRequest', function($scope, $element, $attrs, $parse, dropdownConfig, uibDropdownService, $animate, $position, $document, $compile, $templateRequest) {
  var self = this,
    scope = $scope.$new(), // create a child scope so we are not polluting original one
    templateScope,
    appendToOpenClass = dropdownConfig.appendToOpenClass,
    openClass = dropdownConfig.openClass,
    getIsOpen,
    setIsOpen = angular.noop,
    toggleInvoker = $attrs.onToggle ? $parse($attrs.onToggle) : angular.noop,
    body = $document.find('body'),
    liAutoIncrementId = 0,
    dropdownMenuWatcher;

  $element.addClass('dropdown');

  this.init = function() {
    if ($attrs.isOpen) {
      getIsOpen = $parse($attrs.isOpen);
      setIsOpen = getIsOpen.assign;

      $scope.$watch(getIsOpen, function(value) {
        scope.isOpen = !!value;
      });
    }
  };

  this.toggle = function(open) {
    scope.isOpen = arguments.length ? !!open : !scope.isOpen;
    if (angular.isFunction(setIsOpen)) {
      setIsOpen(scope, scope.isOpen);
    }

    return scope.isOpen;
  };

  // Allow other directives to watch status
  this.isOpen = function() {
    return scope.isOpen;
  };

  this.setDropdownMenu = function (element) {
    // It's itself, so no updates needed
    if (self.dropdownMenu === element) {
      return;
    }

    // Disconnect the old watcher
    if (dropdownMenuWatcher != null) {
      dropdownMenuWatcher.disconnect();
      dropdownMenuWatcher = null;
    }
    self.dropdownMenu = element;
    const role = self.dropdownMenu.attr('role');

    // We need to give a unique id and set the appropriate role
    const setIdAndRole = (li) => {
      // Allow custom IDs (for cypress tests and selectors)
      if (li.getAttribute('id') == null) {
        li.setAttribute('id', `uib-dropdown-${$scope.$id}-li-${liAutoIncrementId++}`);
      }

      if (role === 'menu') {
        li.setAttribute('role', 'presentation');
        // Set the focusable item to role menuitem
        focusable(li).forEach(focusItem => focusItem.setAttribute('role', 'menuitem'));
      } else {
        li.setAttribute('role', 'option');
      }
    };

    // Do this for all the list items
    Array.from(self.dropdownMenu.find('li')).forEach(setIdAndRole);

    // Or if the list items are added later (ng-repeat collection changing, etc)
    dropdownMenuWatcher = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const addedNode of mutation.addedNodes) {
          if (addedNode.tagName === 'LI') {
            setIdAndRole(addedNode);
          }
        }
      }
    });
    dropdownMenuWatcher.observe(self.dropdownMenu[0], { childList: true, subtree: true });
  };

  scope.getToggleElement = function() {
    return self.toggleElement;
  };

  scope.getAutoClose = function() {
    return $attrs.autoClose || 'always'; //or 'outsideClick' or 'disabled'
  };

  scope.getElement = function() {
    return $element;
  };

  /**
   * Focuses on a dropdown entry based on a keyevent
   * @param {KeyboardEvent} event
   */
  scope.handleKeyboardNav = function(event) {
    const role = self.dropdownMenu.attr('role');
    const elements = role === 'listbox' ?
      Array.from(self.dropdownMenu.find('li')) :
      focusable(self.dropdownMenu[0]);

    // Figure out where we are
    const active = self.dropdownMenu[0].querySelector(`.${dropdownConfig.activeItemClass}`);
    const activeIndex = elements.indexOf(active);

    const shouldLoop = role === 'menu';

    const firstIndex = 0;
    const lastIndex = elements.length - 1;
    const nextIndex = (() => {
      switch (event.key) {
        case 'Home':
          return firstIndex;
        case 'End':
          return lastIndex;
        case 'ArrowUp':
        case 'ArrowLeft':
          if (activeIndex === -1) {
            return lastIndex;
          }
          if (activeIndex === firstIndex) {
            return shouldLoop ? lastIndex : firstIndex;
          }
          return activeIndex - 1;
        case 'ArrowDown':
        case 'ArrowRight':
          if (activeIndex === lastIndex) {
            return shouldLoop ? firstIndex : lastIndex;
          }
          return activeIndex + 1;
      }
    })();

    event.preventDefault();
    if (activeIndex !== -1) {
      elements[activeIndex].classList.remove(dropdownConfig.activeItemClass);
    }
    elements[nextIndex].classList.add(dropdownConfig.activeItemClass);
    if (role === 'listbox') {
      self.dropdownMenu.attr('aria-activedescendant', elements[nextIndex].id);
    } else {
      elements[nextIndex].focus();
    }
  };

  scope.getDropdownElement = function() {
    return self.dropdownMenu;
  };

  scope.focusToggleElement = function() {
    if (self.toggleElement) {
      self.toggleElement[0].focus();
    }
  };

  function removeDropdownMenu() {
    if (dropdownMenuWatcher != null) {
      dropdownMenuWatcher.disconnect();
      dropdownMenuWatcher = null;
    }
    $element.append(self.dropdownMenu);
  }

  scope.$watch('isOpen', function(isOpen, wasOpen) {
    var appendTo = null,
      appendToBody = false;

    if (angular.isDefined($attrs.dropdownAppendTo)) {
      var appendToEl = $parse($attrs.dropdownAppendTo)(scope);
      if (appendToEl) {
        appendTo = angular.element(appendToEl);
      }
    }

    if (angular.isDefined($attrs.dropdownAppendToBody)) {
      var appendToBodyValue = $parse($attrs.dropdownAppendToBody)(scope);
      if (appendToBodyValue !== false) {
        appendToBody = true;
      }
    }

    if (appendToBody && !appendTo) {
      appendTo = body;
    }

    if (appendTo && self.dropdownMenu) {
      if (isOpen) {
        appendTo.append(self.dropdownMenu);
        $element.on('$destroy', removeDropdownMenu);
      } else {
        $element.off('$destroy', removeDropdownMenu);
        removeDropdownMenu();
      }
    }

    if (appendTo && self.dropdownMenu) {
      var pos = $position.positionElements($element, self.dropdownMenu, 'bottom-left', true),
        css,
        rightalign,
        scrollbarPadding,
        scrollbarWidth = 0;

      css = {
        top: pos.top + 'px',
        display: isOpen ? 'block' : 'none'
      };

      rightalign = self.dropdownMenu.hasClass('dropdown-menu-right');
      if (!rightalign) {
        css.left = pos.left + 'px';
        css.right = 'auto';
      } else {
        css.left = 'auto';
        scrollbarPadding = $position.scrollbarPadding(appendTo);

        if (scrollbarPadding.heightOverflow && scrollbarPadding.scrollbarWidth) {
          scrollbarWidth = scrollbarPadding.scrollbarWidth;
        }

        css.right = window.innerWidth - scrollbarWidth -
          (pos.left + $element.prop('offsetWidth')) + 'px';
      }

      // Need to adjust our positioning to be relative to the appendTo container
      // if it's not the body element
      if (!appendToBody) {
        var appendOffset = $position.offset(appendTo);

        css.top = pos.top - appendOffset.top + 'px';

        if (!rightalign) {
          css.left = pos.left - appendOffset.left + 'px';
        } else {
          css.right = window.innerWidth -
            (pos.left - appendOffset.left + $element.prop('offsetWidth')) + 'px';
        }
      }

      self.dropdownMenu.css(css);
    }

    var openContainer = appendTo ? appendTo : $element;
    var dropdownOpenClass = appendTo ? appendToOpenClass : openClass;
    var hasOpenClass = openContainer.hasClass(dropdownOpenClass);
    var isOnlyOpen = uibDropdownService.isOnlyOpen($scope, appendTo);

    if (hasOpenClass === !isOpen) {
      var toggleClass;
      if (appendTo) {
        toggleClass = !isOnlyOpen ? 'addClass' : 'removeClass';
      } else {
        toggleClass = isOpen ? 'addClass' : 'removeClass';
      }
      $animate[toggleClass](openContainer, dropdownOpenClass).then(function() {
        if (isOpen) {
          const role = scope.getDropdownElement().attr('role');
          if (role === 'listbox') {
            // First focus on the ul menu
            self.dropdownMenu[0].focus();

            // Set active on the selected item if available, otherwise set active to the first item
            let selected = self.dropdownMenu[0].querySelector('[aria-selected="true"]');
            if (selected == null) {
              selected = self.dropdownMenu.find('li')[0];
            }
            selected.classList.add(dropdownConfig.activeItemClass);
            self.dropdownMenu.attr('aria-activedescendant', selected.id);
          }
          if (role === 'menu') {
            // Focus on the first element when we open
            // We'll just emulate pressing 'Home' when it opens.
            scope.handleKeyboardNav(new KeyboardEvent('keydown', {
              key: 'Home'
            }));
          }
          // If the role is combobox, we'll do nothing
        } else {
          // If we have marked an item as active, unmark it when closing
          var activeEl = self.dropdownMenu[0].querySelector(`.${dropdownConfig.activeItemClass}`);
          if (activeEl != null) {
            activeEl.classList.remove(dropdownConfig.activeItemClass);
          }
          self.dropdownMenu.attr('aria-activedescendant', null);
        }
        if (angular.isDefined(isOpen) && isOpen !== wasOpen) {
          toggleInvoker($scope, { open: !!isOpen });
        }
      });
    }

    if (isOpen) {
      if (self.dropdownMenuTemplateUrl) {
        $templateRequest(self.dropdownMenuTemplateUrl).then(function(tplContent) {
          templateScope = scope.$new();
          $compile(tplContent.trim())(templateScope, function(dropdownElement) {
            var newEl = dropdownElement;
            self.dropdownMenu.replaceWith(newEl);
            self.setDropdownMenu(newEl);
          });
        });
      }

      uibDropdownService.open(scope, self.dropdownMenu, appendTo);
    } else {
      uibDropdownService.close(scope, self.dropdownMenu, appendTo);
      if (self.dropdownMenuTemplateUrl) {
        if (templateScope) {
          templateScope.$destroy();
        }
        var newEl = angular.element('<ul class="dropdown-menu"></ul>');
        self.dropdownMenu.replaceWith(newEl);
        self.setDropdownMenu(newEl);
      }
    }

    if (angular.isFunction(setIsOpen)) {
      setIsOpen($scope, isOpen);
    }
  });
}])

.directive('uibDropdown', function() {
  return {
    controller: 'UibDropdownController',
    link: function(scope, element, attrs, dropdownCtrl) {
      if (attrs.role != null) {
        throw new Error('uib-dropdown cannot have a role attribute. Place role on the child ul element instead.');
      }
      dropdownCtrl.init();
    }
  };
})

.directive('uibDropdownMenu', function() {
  return {
    restrict: 'A',
    require: '?^uibDropdown',
    link: function(scope, element, attrs, dropdownCtrl) {
      if (attrs.role == null) {
        throw new Error('uib-dropdown-menu element is required to have a role attribute.');
      }
      if (!dropdownCtrl || angular.isDefined(attrs.dropdownNested)) {
        return;
      }

      element.addClass('dropdown-menu');
      element.attr('tabindex', '-1');
      // Combobox doesn't have a toggleElement
      if (dropdownCtrl.toggleElement != null) {
        dropdownCtrl.toggleElement.attr('aria-haspopup', attrs.role);
      }

      var tplUrl = attrs.templateUrl;
      if (tplUrl) {
        dropdownCtrl.dropdownMenuTemplateUrl = tplUrl;
      }

      dropdownCtrl.setDropdownMenu(element);
    }
  };
})

.directive('uibDropdownToggle', function() {
  return {
    require: '?^uibDropdown',
    link: function(scope, element, attrs, dropdownCtrl) {
      if (!dropdownCtrl) {
        return;
      }

      element.addClass('dropdown-toggle');

      dropdownCtrl.toggleElement = element;

      var toggleDropdown = function(event) {
        event.preventDefault();

        if (!element.hasClass('disabled') && !attrs.disabled) {
          scope.$apply(function() {
            dropdownCtrl.toggle();
          });
        }
      };

      element.on('click', toggleDropdown);

      // WAI-ARIA
      element.attr({'aria-expanded': false });
      scope.$watch(dropdownCtrl.isOpen, function(isOpen) {
        element.attr('aria-expanded', !!isOpen);
      });

      scope.$on('$destroy', function() {
        element.off('click', toggleDropdown);
      });
    }
  };
});
